import { Context2d, GState } from 'jspdf'
// import { IEditorOption } from '@hufe921/canvas-editor'
import { DeepRequired } from '../../../interface/Common'
import { DrawPdf } from '../DrawPdf'
import { IEditorOption } from '../../../interface/Editor'
import { FORMAT_PLACEHOLDER } from '../../../dataset/constant/PageNumber'
import { WatermarkType } from '../../../dataset/enum/Watermark'
import { PageNumber } from './PageNumber'
import { platform } from '../../../platform/current'

export class Watermark {
  private draw: DrawPdf
  private options: DeepRequired<IEditorOption>
  private imageCache: Map<string, HTMLImageElement>

  constructor(draw: DrawPdf) {
    this.draw = draw
    this.options = <DeepRequired<IEditorOption>>draw.getOptions()
    this.imageCache = new Map()
  }

  public renderText(ctx2d: Context2d, pageNo: number) {
    const {
      watermark: { data, opacity, font, size, color, repeat, gap, numberType },
      scale
    } = this.options
    const width = this.draw.getWidth()
    const height = this.draw.getHeight()
    const fontString = `${size * scale}px ${font}`
    // 开始绘制
    ctx2d.save()
    this.draw.getPdf().setGState(new GState({
      opacity
    }))
    ctx2d.globalAlpha = opacity
    ctx2d.font = fontString
    ctx2d.fillStyle = color
    // 格式化文本
    let text = data
    const pageNoReg = new RegExp(FORMAT_PLACEHOLDER.PAGE_NO)
    if (pageNoReg.test(text)) {
      text = PageNumber.formatNumberPlaceholder(
        text,
        pageNo + 1,
        pageNoReg,
        numberType
      )
    }
    const pageCountReg = new RegExp(FORMAT_PLACEHOLDER.PAGE_COUNT)
    if (pageCountReg.test(text)) {
      text = PageNumber.formatNumberPlaceholder(
        text,
        this.draw.getPageCount(),
        pageCountReg,
        numberType
      )
    }
    // Measure with the same font the watermark draws in (fakeCtx would
    // otherwise measure against whatever font it last held).
    const metrics = this.draw.measureText(fontString, text)
    const textWidth = metrics.width
    const textHeight =
      metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent
    // Draw the -45° rotated text centered on (cx, cy). The baseline offset
    // (ascent - descent) / 2 vertically centers the glyph box on cy.
    const drawRotated = (cx: number, cy: number) => {
      ctx2d.save()
      ctx2d.translate(cx, cy)
      ctx2d.rotate((-45 * Math.PI) / 180)
      ctx2d.fillText(
        text,
        -textWidth / 2,
        (metrics.actualBoundingBoxAscent - metrics.actualBoundingBoxDescent) / 2
      )
      ctx2d.restore()
    }
    if (repeat) {
      // jsPDF's Context2d has no working canvas-pattern fill, so tile manually:
      // step by the rotated text's bounding diagonal (so neighbours don't
      // overlap) plus the configured gap, and stamp the text in each cell.
      const diagonalLength = Math.sqrt(
        Math.pow(textWidth, 2) + Math.pow(textHeight, 2)
      )
      const stepX = diagonalLength + 2 * gap[0] * scale
      const stepY = diagonalLength + 2 * gap[1] * scale
      for (let cy = stepY / 2; cy < height + stepY; cy += stepY) {
        for (let cx = stepX / 2; cx < width + stepX; cx += stepX) {
          drawRotated(cx, cy)
        }
      }
    } else {
      // Single watermark centered on the page.
      drawRotated(width / 2, height / 2)
    }
    this.draw.getPdf().setGState(new GState({
      opacity: 1
    }))
    ctx2d.restore()
  }

  public renderImage(ctx2d: Context2d) {
    const {
      watermark: { width, height, data, opacity, repeat, gap },
      scale
    } = this.options
    if (!this.imageCache.has(data)) {
      // const img = new Image()
      // img.setAttribute('crossOrigin', 'Anonymous')
      // img.src = data
      // img.onload = () => {
      //   this.imageCache.set(data, img)
      //   // 避免层级上浮，触发编辑器二次渲染
      //   this.draw.render({
      //     isCompute: false,
      //     isSubmitHistory: false
      //   })
      // }
      // return
    }
    const docWidth = this.draw.getWidth()
    const docHeight = this.draw.getHeight()
    const imageWidth = width * scale
    const imageHeight = height * scale
    // 开始绘制
    ctx2d.save()
    ctx2d.globalAlpha = opacity
    if (repeat) {
      const dpr = this.draw.getPagePixelRatio()
      // Initial 1x1 — width/height are reset below to the computed pattern size.
      // We can't pass them here because they depend on textMetrics measured above.
      const temporaryCanvas = platform.createCanvas(1, 1)
      const temporaryCtx = temporaryCanvas.getContext('2d')!
      // 勾股定理计算旋转后的宽高对角线尺寸 a^2 + b^2 = c^2
      const diagonalLength = Math.sqrt(
        Math.pow(imageWidth, 2) + Math.pow(imageHeight, 2)
      )
      // 加上 gap 间距
      const patternWidth = diagonalLength + 2 * gap[0] * scale
      const patternHeight = diagonalLength + 2 * gap[1] * scale
      // 宽高设置
      temporaryCanvas.width = patternWidth
      temporaryCanvas.height = patternHeight
      // CSS style is cosmetic-only (sets the displayed canvas size in the DOM).
      // The PDF pipeline only consumes the canvas pixel buffer above, and
      // @napi-rs/canvas has no `.style` — guard so the Node build doesn't crash.
      if (temporaryCanvas.style) {
        temporaryCanvas.style.width = `${patternWidth * dpr}px`
        temporaryCanvas.style.height = `${patternHeight * dpr}px`
      }
      // 旋转45度
      temporaryCtx.translate(patternWidth / 2, patternHeight / 2)
      temporaryCtx.rotate((-45 * Math.PI) / 180)
      temporaryCtx.translate(-patternWidth / 2, -patternHeight / 2)
      // 绘制图片
      temporaryCtx.drawImage(
        this.imageCache.get(data)!,
        (patternWidth - imageWidth) / 2,
        (patternHeight - imageHeight) / 2,
        imageWidth,
        imageHeight
      )
      // 创建平铺模式
      // const pattern = ctx2d.createPattern(temporaryCanvas, 'repeat')
      ctx2d.createPattern()
      // if (pattern) {
      //   ctx2d.fillStyle = pattern
      ctx2d.fillRect(0, 0, docWidth, docHeight)
      // }
    } else {
      // const x = docWidth / 2
      // const y = docHeight / 2
      // ctx2d.translate(x, y)
      // ctx2d.rotate((-45 * Math.PI) / 180)
      // const angle = 45 * Math.PI / 180 // em radianos
      // const centerX = imageWidth / 2
      // const centerY = imageHeight / 2

      // Para rotacionar pelo centro, ajuste x e y:
      // const x = centerX + (imageWidth / 2)
      // const y = centerY + (imageHeight / 2)
      this.draw.getPdf().addImage(
        data,
        'PNG', // ou outro formato
        ((docWidth * 45) / 100),
        ((docHeight * 35) / 100),
        imageWidth,
        imageHeight,
        undefined,
        'FAST',
        45
      )
    }
    ctx2d.restore()
  }

  public render(ctx2d: Context2d, pageNo: number) {
    if (this.options.watermark.type === WatermarkType.IMAGE) {
      this.renderImage(ctx2d)
    } else {
      this.renderText(ctx2d, pageNo)
    }
  }
}